import {
  Dropdown,
  Label,
  Option,
  mergeClasses,
} from "@fluentui/react-components";
import { useId, type FC } from "react";
import { CATEGORY_OPTIONS, SEVERITY_OPTIONS, TIME_OPTIONS } from "./constants";
import type { NotificationFilterState } from "./types";

interface NotificationFilterDropdownClassNames {
  field?: string;
  label?: string;
  dropdown?: string;
  severityDropdown?: string;
  categoryDropdown?: string;
  timeDropdown?: string;
}

export interface NotificationFilterDropdownsProps {
  filters: NotificationFilterState;
  onChange: (nextFilters: NotificationFilterState) => void;
  classNames?: NotificationFilterDropdownClassNames;
  showTimeFilter?: boolean;
}

export const NotificationFilterDropdowns: FC<
  NotificationFilterDropdownsProps
> = ({ filters, onChange, classNames = {}, showTimeFilter = true }) => {
  const severityDropdownId = useId();
  const categoryDropdownId = useId();
  const timeDropdownId = useId();

  const updateFilter = <Key extends keyof NotificationFilterState>(
    key: Key,
    value: NotificationFilterState[Key]
  ): void => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <>
      <div className={classNames.field}>
        <Label className={classNames.label} htmlFor={severityDropdownId}>
          Severity
        </Label>
        <Dropdown
          id={severityDropdownId}
          className={mergeClasses(
            classNames.dropdown,
            classNames.severityDropdown
          )}
          value={
            SEVERITY_OPTIONS.find(({ value }) => value === filters.severity)
              ?.label
          }
          selectedOptions={[filters.severity]}
          onOptionSelect={(_, data) =>
            updateFilter(
              "severity",
              data.optionValue as NotificationFilterState["severity"]
            )
          }
        >
          {SEVERITY_OPTIONS.map(({ value, label }) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Dropdown>
      </div>

      <div className={classNames.field}>
        <Label className={classNames.label} htmlFor={categoryDropdownId}>
          Category
        </Label>
        <Dropdown
          id={categoryDropdownId}
          className={mergeClasses(
            classNames.dropdown,
            classNames.categoryDropdown
          )}
          value={
            CATEGORY_OPTIONS.find(({ value }) => value === filters.category)
              ?.label
          }
          selectedOptions={[filters.category]}
          onOptionSelect={(_, data) =>
            updateFilter(
              "category",
              data.optionValue as NotificationFilterState["category"]
            )
          }
        >
          {CATEGORY_OPTIONS.map(({ value, label }) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Dropdown>
      </div>

      {showTimeFilter && (
        <div className={classNames.field}>
          <Label className={classNames.label} htmlFor={timeDropdownId}>
            Time
          </Label>
          <Dropdown
            id={timeDropdownId}
            className={mergeClasses(
              classNames.dropdown,
              classNames.timeDropdown
            )}
            value={
              TIME_OPTIONS.find(({ value }) => value === filters.time)?.label
            }
            selectedOptions={[filters.time]}
            onOptionSelect={(_, data) =>
              updateFilter(
                "time",
                data.optionValue as NotificationFilterState["time"]
              )
            }
          >
            {TIME_OPTIONS.map(({ value, label }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Dropdown>
        </div>
      )}
    </>
  );
};
